// @ts-nocheck
import { ContentState } from "draft-js";
import { List } from "immutable";
import { isArray } from "lodash";
import { Observable, WritableObservable } from "micro-observables";
import {
	ArticleDraft,
	ArticleSection,
	ArticleSectionType,
	ArticleStatus,
	AttachmentSection,
	ImageSection,
	LoadingSection,
	SubtitleSection,
	TextSection,
	VideoSection,
} from "@domain/lms/article/article";
import { Category, SubCategory } from "@domain/lms/category/category";
import { Author } from "@domain/lms/content";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { ContentVisibility } from "@domain/lms/visibility";
import { Media } from "@domain/media/media";
import { withId } from "@utils/domain";
import { AttachmentSectionController } from "@ui/pages/lms/article/articleEditorPage/sectionEditor/attachmentSectionEditor/attachmentSectionController";
import { ImageSectionController } from "@ui/pages/lms/article/articleEditorPage/sectionEditor/imageSectionEditor/imageSectionController";
import { SubtitleSectionController } from "@ui/pages/lms/article/articleEditorPage/sectionEditor/subtitleSectionEditor/subtitleSectionController";
import { TextSectionController } from "@ui/pages/lms/article/articleEditorPage/sectionEditor/textSectionEditor/textSectionController";
import { VideoSectionController } from "@ui/pages/lms/article/articleEditorPage/sectionEditor/videoSectionEditor/videoSectionController";

export class ArticleEditorController {
	private previousValues: ArticleDraft[];
	private readonly currentValueIndex = new WritableObservable(0);

	readonly article: Observable<ArticleDraft>;

	constructor(initialArticleDraft: ArticleDraft) {
		this.previousValues = [initialArticleDraft];
		this.article = this.currentValueIndex.transform((index) => this.previousValues[index]);
	}

	setTitle(title: string): void {
		this.updateArticle({ title });
	}
	setDescription(description: ContentState): void {
		this.updateArticle({ description });
	}
	setBanner(banner?: Media): void {
		this.updateArticle({ banner });
	}
	setIntroduction(introduction?: Media): void {
		this.updateArticle({ introduction });
	}
	setDuration(duration: number): void {
		this.updateArticle({ duration });
	}
	setCategory(category?: Category): void {
		this.updateArticle({ category });
	}
	setArticleType(articleType?: "NEWS" | "INSIGHT"): void {
		this.updateArticle({ articleType });
	}
	setSubCategory(subCategory?: SubCategory): void {
		this.updateArticle({ subCategory });
	}
	setStatus(status: ArticleStatus): void {
		this.updateArticle({ status });
	}
	setLanguage(language: ContentLanguage): void {
		this.updateArticle({ language });
	}

	setPublisher(publisher?: Author): void {
		this.updateArticle({ publisher });
	}
	setOwnership(keepOwnership?: boolean): void {
		this.updateArticle({ keepOwnership });
	}
	setVisibility(visibility: ContentVisibility): void {
		this.updateArticle({ visibility });
	}

	toggleWorkspace(id: string): void {
		const article = this.article.get();
		const workspaceIds = article.workspaceIds.update((ids) => (ids.has(id) ? ids.remove(id) : ids.add(id)));
		this.updateArticle({ workspaceIds });
	}

	isValidForDraft(): boolean {
		const article = this.article.get();
		return article.title.trim().length > 0;
	}

	private allTextsAreNotEmpty(sections: ArticleSection[]): boolean {
		let i = 0;
		while (i < sections.length) {
			const section = sections[i];
			// Disable condition in field getPlaintext
			//if (section.type === "TEXT" && section.content.getPlainText().trim().length === 0) return false;
			if (section.type === "SUBTITLE" && section.title.trim().length === 0) return false;
			i++;
		}
		return true;
	}

	isValidForNextStep(): boolean {
		const article = this.article.get();
		return (
			article.title.trim().length > 0 &&
			!article.content.sections.isEmpty() &&
			this.allTextsAreNotEmpty(article.content.sections.toArray()) &&
			//firstArticleSection!.title.length > 0 &&
			(article.visibility === "PUBLIC" || article.workspaceIds.size > 0) &&
			!article.content.sections.find((s) => s.type === "LOADING")
		);
	}

	isValidForPublication(): boolean {
		const article = this.article.get();
		return (
			this.isValidForNextStep() && /*article.description.hasText() &&*/ article.category != null && article.duration > 0
		);
	}

	addArticleSection(type): void {
		this.updateArticle({
			content: {
				...this.article.get().content,
				sections: this.article.get().content.sections.push(
					withId({
						type: type,
					})
				),
			},
		});
	}

	updateArticle(articleUpdate: Partial<ArticleDraft>): void {
		const article: ArticleDraft = { ...this.article.get(), ...articleUpdate };
		this.previousValues = [...this.previousValues.slice(0, this.currentValueIndex.get() + 1), article];
		this.currentValueIndex.set(this.currentValueIndex.get() + 1);
	}

	getSectionsList(): List<ArticleSection> {
		const sections = this.article.get().content.sections;
		if (isArray(sections)) {
			return List<ArticleSection>(sections);
		}
		return sections;
	}

	moveSection(fromArticleSectionId: string, toArticleSectionId: string, fromIndex: number, toIndex: number): void {
		let sections = this.getSectionsList();
		const movedSection = sections.get(fromIndex);
		if (!movedSection) {
			return;
		}
		sections = sections.remove(fromIndex);
		sections = sections.insert(toIndex, movedSection);
		this.updateArticleSections(sections);
	}

	private updateArticleSections(sections: List<ArticleSection>): void {
		const currentContent = this.article.get().content;
		this.updateArticle({ content: { ...currentContent, sections } });
	}

	private updateAssets(assets: List<Media>): void {
		const currentContent = this.article.get().content;
		this.updateArticle({ content: { ...currentContent, assets } });
	}

	addAsset(media: Media): void {
		this.updateArticle({
			content: {
				...this.article.get().content,
				assets: this.article.get().content.assets.push(media),
			},
		});
	}

	private updateArticleSection(
		id: string,
		update: (current: ArticleSection | LoadingSection) => ArticleSection | LoadingSection
	): void {
		this.updateArticleSections(this.getSectionsList().map((c) => (c.id === id ? update(c) : c)));
	}

	private removeArticleSection(id: string): void {
		const article = this.article.get();
		const index = article.content.sections.findIndex((s) => s.id === id);
		if (index < 0) {
			return;
		}
		this.updateArticleSections(this.getSectionsList().remove(index));
	}

	removeAssetById(id: string): void {
		const article = this.article.get();
		const index = article.content.assets.findIndex((s) => s.id === id);
		if (index < 0) {
			return;
		}
		this.updateAssets(this.article.get().content.assets.remove(index));
	}

	addSection(type: "TEXT" | "SUBTITLE"): void;
	addSection(type: "IMAGE", assetId: string): void;
	addSection(type: "PDF", assetId: string): void;
	addSection(type: "VIDEO", assetId?: string): void;
	addSection(type: "ATTACHMENT", assetId: string, fileName: string): void;
	addSection(type: ArticleSectionType, assetId?: string, fileName?: string): void {
		this.updateArticleSections(
			List([
				...this.getSectionsList(),
				withId(
					type === "TEXT"
						? { type, content: ContentState.createFromText("") }
						: type === "VIDEO"
							? { type, ...(assetId ? { assetId } : { url: "" }) }
							: type === "SUBTITLE"
								? { type, title: "" }
								: type === "ATTACHMENT"
									? { type, assetId: assetId!, title: fileName! }
									: { type, assetId: assetId! }
				),
			])
		);
	}

	addLoadingSection(taskId: string): void {
		const sections = this.getSectionsList();
		sections.push({ type: "LOADING", id: taskId });
		this.updateArticleSections(sections);
	}

	addOrReplaceSection(type: "VIDEO", assetId: string, taskId: string): void {
		let sections = this.getSectionsList();
		const loadingIndex = sections.findIndex((section) => section.type === "LOADING" && section.id === taskId);
		if (loadingIndex >= 0) {
			sections = sections.set(loadingIndex, withId({ type, assetId }));
		}
		sections = sections.push(withId({ type, assetId }));
		this.updateArticleSections(sections);
	}

	private updateSection<T extends ArticleSection>(id: string, update: (section: T) => T): void {
		let sections = this.getSectionsList();
		sections = sections.map((s) => (s.id === id ? update(s as T) : s));
		this.updateArticleSections(sections);
	}

	removeSection(id: string): void {
		let sections = this.getSectionsList();

		const index = sections.findIndex((s) => s.id === id);
		if (index >= 0) {
			sections = sections.remove(index);
			this.updateArticleSections(sections);
		}
	}

	videoSectionController(section: VideoSection): VideoSectionController {
		return new VideoSectionController(
			(updater) => this.updateSection(section.id, updater),
			() => this.removeSection(section.id)
		);
	}
	textSectionController(section: TextSection): TextSectionController {
		return new TextSectionController(
			(updater) => this.updateSection(section.id, updater),
			() => this.removeSection(section.id)
		);
	}
	imageSectionController(section: ImageSection): ImageSectionController {
		return new ImageSectionController(
			(updater) => this.updateSection(section.id, updater),
			() => this.removeSection(section.id)
		);
	}
	attachmentSectionController(section: AttachmentSection): AttachmentSectionController {
		return new AttachmentSectionController(
			(updater) => this.updateSection(section.id, updater),
			() => this.removeSection(section.id)
		);
	}
	subtitleSectionController(section: SubtitleSection): SubtitleSectionController {
		return new SubtitleSectionController(
			(updater) => this.updateSection(section.id, updater),
			() => this.removeSection(section.id)
		);
	}
}
